import React, { useContext, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { dispatchAnalyticsTrack } from "~/lib/segment";
import { getFirstUserFacingError, inVideocallMode } from "~/lib/utils";
import AskFellowPanel from "~/nav/panels/AskFellow";
import { useAskFellowButton, useCreateConversationMutation } from "~/nav/panels/AskFellow/AskFellowHooks";
import { Modal } from "~/ui/legacyFellowIndex";
import { useModals } from "~/ui/modals";

import type { AskFellowHooks_useAskFellowButton_note$key } from "~/__generated__/AskFellowHooks_useAskFellowButton_note.graphql";

import styles from "./FloatingRecordingControls.module.css";

interface AskFellowActionContextValue {
	getActionHandler: () => () => void;
	actionType: "nav-panel" | "meeting-modal" | "videocall";
	isAskFellowOpen: boolean;
	setIsAskFellowOpen: (isAskFellowOpen: boolean) => void;
	inModal: boolean;
}

const AskFellowActionContext = React.createContext<AskFellowActionContextValue | null>(null);

interface AskFellowActionProviderProps {
	children: React.ReactNode;
	note: AskFellowHooks_useAskFellowButton_note$key | null;
	noteId?: string;
	inModal?: boolean;
}

export function AskFellowActionProvider({
	children,
	note: noteKey,
	noteId,
	inModal = false,
}: AskFellowActionProviderProps) {
	const [isAskFellowOpen, setIsAskFellowOpen] = useState(false);

	const modals = useModals();
	const [createConversation, mutationInProgress] = useCreateConversationMutation();

	const navPanelHandler = useAskFellowButton({ noteKey, source: "floating_note_button" });

	const meetingModalHandler = React.useCallback(async () => {
		if (mutationInProgress) return;

		try {
			await createConversation(noteId);
			setIsAskFellowOpen(true);
			dispatchAnalyticsTrack("[Ask Copilot] Modal opened", { source: "floating_note_button" });
		} catch (e) {
			const message = getFirstUserFacingError(e) ?? "Something went wrong; please try again later.";
			toast.error(message);
		}
	}, [noteId, createConversation, mutationInProgress]);

	const videocallHandler = React.useCallback(() => {
		modals.open(({ close }) => (
			<Modal onClose={close} className={styles.askFellowModal}>
				<Modal.Body className={styles.askFellowModalBody}>
					<AskFellowPanel contextId={noteId} compactLandingPage={true} onClose={close} />
				</Modal.Body>
			</Modal>
		));
	}, [noteId, modals]);

	// Determine action type based on context
	const actionType: AskFellowActionContextValue["actionType"] = useMemo(() => {
		if (inModal && !inVideocallMode) {
			return "meeting-modal";
		} else if (inVideocallMode) {
			return "videocall";
		} else {
			return "nav-panel";
		}
	}, [inModal]);

	const getActionHandler = React.useCallback(() => {
		switch (actionType) {
			case "meeting-modal":
				// Set isAskFellowOpen to true and create Ask Fellow conversation when in a meeting modal
				return meetingModalHandler;
			case "videocall": {
				// Open Ask Fellow in a modal when in a video call
				return videocallHandler;
			}
			case "nav-panel":
			default:
				// Open Ask Fellow in the nav panel in all other cases
				return navPanelHandler;
		}
	}, [actionType, meetingModalHandler, videocallHandler, navPanelHandler]);

	const contextValue = useMemo<AskFellowActionContextValue>(
		() => ({
			getActionHandler,
			actionType,
			isAskFellowOpen,
			setIsAskFellowOpen,
			inModal,
		}),
		[getActionHandler, actionType, isAskFellowOpen, setIsAskFellowOpen, inModal],
	);

	return <AskFellowActionContext.Provider value={contextValue}>{children}</AskFellowActionContext.Provider>;
}

export function useAskFellowActionContext(): AskFellowActionContextValue {
	const context = useContext(AskFellowActionContext);
	if (!context) {
		throw new Error("useAskFellowActionContext must be used within AskFellowActionProvider");
	}
	return context;
}
